import { useContext } from 'react';
import { AppContext } from '../App';
import { AlertTriangle, CreditCard, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Reads the membership status fetched once per session in App.js (see contextValue there)
// instead of fetching it again — every page used to do its own independent request here,
// which both wasted requests and meant "still loading" (or "request failed") got treated
// the same as "membership is blocked" (isBlocked defaulted to true whenever status was
// null). isBlocked/isLimitReached now only turn true once we have a confirmed response.
export function useMembershipStatus() {
  const { membershipStatus: status, membershipLoading: loading, refetchMembership } = useContext(AppContext);

  const isBlocked = !!status && (!status.has_membership || status.bloqueado);
  const isLimitReached = !!status && status.has_membership && status.cantidad_consentimientos !== -1 && status.consentimientos_disponibles <= 0;

  return { status, loading, isBlocked, isLimitReached, refetchMembership };
}

export function MembershipBlockedOverlay({ mode = 'full' }) {
  const navigate = useNavigate();

  if (mode === 'full') {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4" data-testid="membership-blocked-overlay">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
          <Lock className="text-red-500" size={40} />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-3 text-center">Membresía Inactiva o Vencida</h2>
        <p className="text-slate-600 text-center max-w-md mb-6">
          Su membresía está vencida o bloqueada. Realice el pago para continuar utilizando esta funcionalidad.
        </p>
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-colors"
          data-testid="btn-ir-a-pagar"
        >
          <CreditCard size={20} />
          Ir a Pagar Membresía
        </button>
      </div>
    );
  }

  return (
    <div className="mb-6 bg-amber-50 border-2 border-amber-200 rounded-xl p-4" data-testid="membership-warning-banner">
      <div className="flex items-center gap-3">
        <AlertTriangle className="text-amber-600 flex-shrink-0" size={22} />
        <div className="flex-1">
          <p className="text-amber-800 font-semibold text-sm">Su membresía está vencida o bloqueada.</p>
          <p className="text-amber-700 text-xs mt-0.5">Realice el pago desde el Dashboard para evitar interrupciones en el servicio.</p>
        </div>
        <button
          onClick={() => navigate('/dashboard')}
          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-lg transition-colors"
          data-testid="btn-ir-a-pagar-banner"
        >
          <CreditCard size={14} />
          Pagar
        </button>
      </div>
    </div>
  );
}

export function ConsentLimitOverlay() {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4" data-testid="consent-limit-overlay">
      <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mb-6">
        <AlertTriangle className="text-amber-500" size={40} />
      </div>
      <h2 className="text-2xl font-bold text-slate-900 mb-3 text-center">Límite de Consentimientos Alcanzado</h2>
      <p className="text-slate-600 text-center max-w-md mb-6">
        Ha utilizado todos los consentimientos de su plan. Renueve su membresía o actualice su plan para continuar creando consentimientos.
      </p>
      <button
        onClick={() => navigate('/dashboard')}
        className="flex items-center gap-2 px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl transition-colors"
        data-testid="btn-ir-a-upgrade"
      >
        <CreditCard size={20} />
        Renovar o Cambiar Plan
      </button>
    </div>
  );
}
